import { Injectable } from '@angular/core';
import { usesEpik, usesTamaro } from '@fmnts/api';
import type { TransactionInfo } from '@raisenow/epik';
import * as O from 'effect/Option';
import type { ReadonlyRecord } from 'effect/Record';
import { isString } from 'effect/String';
import { Subject, distinctUntilChanged, map, shareReplay } from 'rxjs';
import { RaiseNowExtraFields } from '../raisenow-iframe/raisenow-util.service';

/**
 * Describes which integration of raisenow payments is used.
 */
export enum Integration {
  /** Uses the legacy integration via `@raisenow/epik`. */
  Epik = 'epik',
  /** Payment via tamaro. */
  Tamaro = 'tamaro',
}

interface RaiseNowEpikResult {
  integration: Integration.Epik;
  /** RaiseNow transaction info */
  transactionInfo: TransactionInfo;
}

interface RaiseNowTamaroResult {
  integration: Integration.Tamaro;
}

export type RaiseNowModalResult = RaiseNowTamaroResult | RaiseNowEpikResult;

@Injectable({
  providedIn: 'root',
})
export class RaiseNowModalService {
  // TODO: pass directly as modal data, once the raisenow modals use `Modal`.
  private _currentModalData: O.Option<
    Readonly<{
      formData: ReadonlyRecord<string, unknown>;
      raiseNowExtraFields: Readonly<RaiseNowExtraFields>;
    }>
  > = O.none();

  /**
   * Emits with the `flow` of the modal that should be shown.
   * If modal is closed `null` is emitted.
   */
  private readonly _modalShown$ = new Subject<Integration | null>();

  /**
   * Emits `true` when EPP modal is shown
   */
  public readonly openEPPModal = this._modalShown$.pipe(
    map((integration) => Integration.Epik === integration),
    distinctUntilChanged(),
  );

  /**
   * Emits `true` when Tamaro modal is shown
   */
  public readonly openTamaro = this._modalShown$.pipe(
    map((integration) => Integration.Tamaro === integration),
    distinctUntilChanged(),
    shareReplay(),
  );

  getFormData(): ReadonlyRecord<string, any> {
    return O.getOrThrow(this._currentModalData).formData;
  }

  get raiseNowExtraFields() {
    return O.getOrThrow(this._currentModalData).raiseNowExtraFields;
  }

  /**
   * Will initialize formData and check the passed `payment_method`.
   * Depending on which method was chosen, will open a new modal.
   *
   * @param formData
   * For `Integration.Epik` formData will consist of the available form
   * passed in by user
   *
   * For `Integration.Tamaro` formData will contain additional values, sent by
   * the backend.
   *
   * @param raiseNowExtraFields
   * Used for `Integration.Epik`, to get missing values
   */
  open(
    formData: Record<string, unknown>,
    raiseNowExtraFields: RaiseNowExtraFields = {},
  ) {
    this._currentModalData = O.some({
      formData,
      raiseNowExtraFields,
    });

    const integration = getIntegration(formData);
    this._modalShown$.next(
      O.getOrThrowWith(
        integration,
        () => new Error('RaiseNow integration could not be determined.'),
      ),
    );
  }

  close() {
    this._modalShown$.next(null);
  }
}

/**
 * Helper to get the integration from the form data
 */
function getIntegration(
  formData: Record<string, unknown>,
): O.Option<Integration> {
  const paymentMethod = formData.payment_method;

  if (isString(paymentMethod)) {
    if (usesTamaro(paymentMethod)) {
      return O.some(Integration.Tamaro);
    } else if (usesEpik(paymentMethod)) {
      return O.some(Integration.Epik);
    }
  }

  return O.none();
}
